import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  useResponsive,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { giftCardRedemptionCtaMessages as messages } from './GiftCardRedemptionCta.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardRedemptionCta = (props: GiftCardRedemptionCtaProps) => {
  const { redeemId } = props;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { currentBreakpoint } = useResponsive();
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToPaymentAndGiftCardScreen = useCallback(() => {
    if (currentBreakpoint.isXS) {
      if (isExpoRouterEnabled) {
        router?.navigate(`/redeem-gift-card/${redeemId}`);
        return;
      }

      navigation?.navigate('Modal', {
        screen: 'RedeemGiftCard',
        params: { giftCardId: redeemId },
      });
      return;
    }

    if (isInStoreOrdersEnabled) {
      if (isExpoRouterEnabled) {
        router?.navigate(`/account/gift-cards/${redeemId}`);
        return;
      }

      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: {
          screen: 'GiftCards',
          params: { giftCardId: redeemId },
        },
      });
      return;
    }

    if (isExpoRouterEnabled) {
      router?.navigate({ pathname: '/account/payment-and-gift-cards', params: { giftCardId: redeemId } }); // prettier-ignore
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'AccountTab',
      params: {
        screen: 'PaymentAndGiftCards',
        params: { giftCardId: redeemId },
      },
    });
  }, [
    currentBreakpoint.isXS,
    isInStoreOrdersEnabled,
    isExpoRouterEnabled,
    router,
    navigation,
    redeemId,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Button size="large" onPress={navigateToPaymentAndGiftCardScreen}>
      <FormattedMessage {...messages.label} />
    </Button>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardRedemptionCtaProps = {
  redeemId: string;
};
